import {HttpBackend, HttpClient} from '@angular/common/http';
import {TranslateLoader, TranslationObject} from '@ngx-translate/core';
import {Observable, catchError, defer, finalize, forkJoin, map, of, shareReplay, tap} from 'rxjs';

export type TranslationBundle = 'audio' | 'translator' | 'detector' | 'paraphraser' | 'presentations' | 'plans' | 'teaser';
const SHARED_BUNDLES = ['', 'recipes', 'images', 'upgrade'];

/** Shared dictionaries at startup; feature dictionaries once their UI is used. */
export class FeatureTranslateLoader extends TranslateLoader {
    readonly requestedLanguages = new Set<string>();
    private readonly activeBundles = new Set<TranslationBundle>();
    private readonly cache = new Map<string, TranslationObject>();
    private readonly pending = new Map<string, Observable<TranslationObject>>();
    private readonly http: HttpClient;

    constructor(backend: HttpBackend) {
        super();
        this.http = new HttpClient(backend);
    }

    activate(bundles: TranslationBundle[]): void {
        bundles.forEach(bundle => this.activeBundles.add(bundle));
    }

    getTranslation(lang: string): Observable<TranslationObject> {
        this.requestedLanguages.add(lang);
        return this.load(lang, [...SHARED_BUNDLES, ...this.activeBundles]);
    }

    loadFeatures(lang: string, bundles: TranslationBundle[]): Observable<TranslationObject> {
        return this.load(lang, bundles);
    }

    hasBundle(lang: string, bundle: TranslationBundle): boolean {
        return this.cache.has(`${lang}:${bundle}`);
    }

    hasKey(lang: string, bundle: TranslationBundle, key: string): boolean {
        return this.cache.get(`${lang}:${bundle}`)?.[key] != null;
    }

    private load(lang: string, bundles: string[]): Observable<TranslationObject> {
        return forkJoin(bundles.map(bundle => {
            const request = this.loadBundle(lang, bundle);
            return bundle ? request.pipe(catchError(() => of({}))) : request;
        })).pipe(map(dictionaries => Object.assign({}, ...dictionaries) as TranslationObject));
    }

    private loadBundle(lang: string, bundle: string): Observable<TranslationObject> {
        return defer(() => {
            const key = `${lang}:${bundle}`;
            const cached = this.cache.get(key);
            if (cached) return of(cached);
            const pending = this.pending.get(key);
            if (pending) return pending;
            const request = this.http.get<TranslationObject>(`assets/i18n/${bundle ? bundle + '/' : ''}${lang}.json`).pipe(
                tap(dictionary => this.cache.set(key, dictionary)),
                finalize(() => this.pending.delete(key)),
                shareReplay({bufferSize: 1, refCount: true}),
            );
            this.pending.set(key, request);
            return request;
        });
    }
}
